// src/components/StaffVaccineReservationList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faCircle } from "@fortawesome/free-solid-svg-icons";
import DataTable from "./AppDataTable";
import { tableCustomStyles } from "../themes/TableStyles";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";
import { Search, Clear } from "../themes/IconButtonSet";
import { StandardHeader } from "../themes/StandardHeader";
import { convertToThDate } from "./CustomDatePicker";

const statusConfig = {
  ใบจองใหม่: { color: "blue.500", label: "ใบจองใหม่" },
  รอการอนุมัติ: { color: "orange.500", label: "รอการอนุมัติ" },
  ยืนยันการจอง: { color: "green.500", label: "ยืนยันการจอง" },
  พร้อมรับวัคซีน: { color: "pink.500", label: "พร้อมรับวัคซีน" },
  รับวัคซีนแล้ว: { color: "purple.500", label: "รับวัคซีนแล้ว" },
  ยกเลิกการจอง: { color: "red.500", label: "ยกเลิกการจอง" },
  ปฏิเสธการจอง: { color: "red.600", label: "ปฏิเสธการจอง" },
  เกินกำหนดรับ: { color: "gray.500", label: "เกินกำหนดรับ" },
};

const StatusBadgeWithDot = ({ status }) => {
  const config = statusConfig[status] || { color: "gray.500" };

  return (
    <HStack spacing={2} align="center">
      <Box color={config.color} display="flex" alignItems="center">
        <FontAwesomeIcon icon={faCircle} size="xs" />
      </Box>
      <Text>{status}</Text>
    </HStack>
  );
};

const statusOptions = [
  "ใบจองใหม่",
  "รอการอนุมัติ",
  "ยืนยันการจอง",
  "พร้อมรับวัคซีน",
  "รับวัคซีนแล้ว",
  "ยกเลิกการจอง",
];

const getValidPeriods = (periods) => {
  const today = new Date();
  return periods.filter((period) => new Date(period.PeriodStart) <= today);
};

const getPeriodStartTime = (period) => new Date(period.PeriodStart).getTime();

const findPeriodByName = (periods, deliveryPeriod) =>
  periods.find((p) => p.DeliveryPeriod === deliveryPeriod);

const sortPeriodsAsc = (periods) =>
  [...periods].sort((a, b) => getPeriodStartTime(a) - getPeriodStartTime(b));

const getEndPeriodOptions = (periods, startDeliveryPeriod) => {
  if (!startDeliveryPeriod) {
    return periods;
  }
  const startPeriod = findPeriodByName(periods, startDeliveryPeriod);
  if (!startPeriod) {
    return periods;
  }
  const startTime = getPeriodStartTime(startPeriod);
  return sortPeriodsAsc(
    periods.filter((p) => getPeriodStartTime(p) >= startTime)
  );
};

const isPeriodAfter = (periods, deliveryPeriodA, deliveryPeriodB) => {
  const periodA = findPeriodByName(periods, deliveryPeriodA);
  const periodB = findPeriodByName(periods, deliveryPeriodB);
  if (!periodA || !periodB) {
    return deliveryPeriodA > deliveryPeriodB;
  }
  return getPeriodStartTime(periodA) > getPeriodStartTime(periodB);
};

const getDefaultFiltersFromPeriods = (periods) => {
  const validPeriods = getValidPeriods(periods);
  const recentPeriods = validPeriods.slice(0, 2);
  const endPeriod = recentPeriods[0]?.DeliveryPeriod || "";
  const startPeriod =
    recentPeriods.length >= 2
      ? recentPeriods[1].DeliveryPeriod
      : endPeriod;

  return {
    StartDeliveryPeriod: startPeriod,
    EndDeliveryPeriod: endPeriod,
    PickupPoint: "",
    ReservationStatus: "",
    ReservationID: "",
    FarmerName: "",
  };
};

const emptyFilters = {
  StartDeliveryPeriod: "",
  EndDeliveryPeriod: "",
  PickupPoint: "",
  ReservationStatus: "",
  ReservationID: "",
  FarmerName: "",
};

const StaffVaccineReservationList = () => {
  const navigate = useNavigate();
  const { sessionId } = useAuth();

  const [reservations, setReservations] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [deliveryPeriods, setDeliveryPeriods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterData, setFilterData] = useState(emptyFilters);

  const columns = [
    {
      name: "เลขที่ใบจอง",
      selector: (row) => row.ReservationID,
      sortable: true,
      width: "180px",
    },
    {
      name: "ชื่อเกษตรกร",
      selector: (row) => row.FarmerName,
      sortable: true,
    },
    {
      name: "รหัสเกษตรกร",
      selector: (row) => row.FarmerID || "-",
      sortable: true,
      width: "180px",
    },
    {
      name: "เบอร์โทร",
      selector: (row) => row.FarmerTel || "-",
      sortable: true,
      width: "150px",
    },
    {
      name: "รอบการจัดส่ง",
      selector: (row) => row.DeliveryPeriod || "-",
      sortable: true,
    },
    {
      name: "จุดจำหน่าย",
      selector: (row) => {
        const point = pickupPoints.find((p) => p.WarehouseID === row.PickupPoint);
        return point ? point.WarehouseName : row.PickupPoint || "-";
      },
      sortable: true,
    },
    {
      name: "วันที่จอง",
      selector: (row) => convertToThDate(row.ReservationDate),
      sortable: true,
      width: "180px",
    },
    {
      name: "สถานะ",
      cell: (row) => <StatusBadgeWithDot status={row.ReservationStatus} />,
      sortable: true,
      width: "200px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() =>
            navigate(`/staffvaccinereservationdetail/${row.ReservationID}`)
          }
          label="ดูรายละเอียด"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPoints(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  const fetchDeliveryPeriods = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationPeriodList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const periods = response.DeliveryPeriods || [];
        const validPeriods = getValidPeriods(periods);
        setDeliveryPeriods(validPeriods);
        return validPeriods;
      }
    } catch (error) {
      console.error("Error fetching delivery periods:", error);
    }
    return [];
  };

  const fetchReservations = async (filters = filterData) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationsList",
        data: {
          StartDeliveryPeriod: filters.StartDeliveryPeriod || "",
          EndDeliveryPeriod: filters.EndDeliveryPeriod || "",
          PickupPoint: filters.PickupPoint || "",
          ReservationStatus: filters.ReservationStatus || "",
          ReservationID: filters.ReservationID?.trim() || "",
          FarmerName: filters.FarmerName?.trim() || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setReservations(response.VaccineReservationsList || []);
      } else {
        setReservations([]);
      }
    } catch (error) {
      console.error("Error fetching reservations:", error);
      setReservations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const initPage = async () => {
    setIsLoading(true);
    try {
      const [, periods] = await Promise.all([
        fetchPickupPoints(),
        fetchDeliveryPeriods(),
      ]);
      const defaultFilters = getDefaultFiltersFromPeriods(periods);
      setFilterData(defaultFilters);
      await fetchReservations(defaultFilters);
    } catch (error) {
      console.error("Error initializing page:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      initPage();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prev) => {
      const updated = { ...prev, [name]: value };
      if (
        name === "StartDeliveryPeriod" &&
        value &&
        prev.EndDeliveryPeriod &&
        isPeriodAfter(deliveryPeriods, value, prev.EndDeliveryPeriod)
      ) {
        updated.EndDeliveryPeriod = value;
      }
      return updated;
    });
  };

  const handleSearch = () => {
    fetchReservations(filterData);
  };

  const handleClearSearch = async () => {
    const periods = deliveryPeriods.length
      ? deliveryPeriods
      : await fetchDeliveryPeriods();
    const resetFilters = getDefaultFiltersFromPeriods(periods);
    setFilterData(resetFilters);
    fetchReservations(resetFilters);
  };

  const handleCreateNew = () => {
    navigate("/staffvaccinereservationdetail/new");
  };

  const endPeriodOptions = getEndPeriodOptions(
    deliveryPeriods,
    filterData.StartDeliveryPeriod
  );

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <StandardHeader
          title="รายการใบจองวัคซีน (สำหรับเจ้าหน้าที่)"
          bgColor="iBlue.600"
          icon={faFileLines}
        />

        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} w="100%">
          <StandardHeader
            title="ค้นหาข้อมูลใบจองวัคซีน"
            bgColor="iBlue.600"
            icon={faSearch}
          />
          <VStack spacing={6} p={5}>
            <HStack spacing={6} w="100%" flexWrap="wrap">
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  รอบการจัดส่ง (ตั้งแต่)
                </FormLabel>
                <Select
                  name="StartDeliveryPeriod"
                  value={filterData.StartDeliveryPeriod}
                  onChange={handleInputChange}
                  placeholder="เลือกรอบเริ่มต้น"
                  colorScheme="iBlue"
                >
                  {deliveryPeriods.map((period) => (
                    <option
                      key={period.DeliveryPeriod}
                      value={period.DeliveryPeriod}
                    >
                      {period.DeliveryPeriod}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  รอบการจัดส่ง (ถึง)
                </FormLabel>
                <Select
                  name="EndDeliveryPeriod"
                  value={filterData.EndDeliveryPeriod}
                  onChange={handleInputChange}
                  placeholder="เลือกรอบสิ้นสุด"
                  colorScheme="iBlue"
                >
                  {endPeriodOptions.map((period) => (
                    <option
                      key={period.DeliveryPeriod}
                      value={period.DeliveryPeriod}
                    >
                      {period.DeliveryPeriod}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  จุดจำหน่าย
                </FormLabel>
                <Select
                  name="PickupPoint"
                  value={filterData.PickupPoint}
                  onChange={handleInputChange}
                  placeholder="เลือกจุดจำหน่าย (ทั้งหมด)"
                  colorScheme="iBlue"
                >
                  {pickupPoints.map((point) => (
                    <option key={point.WarehouseID} value={point.WarehouseID}>
                      {point.WarehouseName}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  สถานะใบจอง
                </FormLabel>
                <Select
                  name="ReservationStatus"
                  value={filterData.ReservationStatus}
                  onChange={handleInputChange}
                  placeholder="เลือกสถานะ (ทั้งหมด)"
                  colorScheme="iBlue"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  เลขที่ใบจอง
                </FormLabel>
                <Input
                  name="ReservationID"
                  value={filterData.ReservationID}
                  onChange={handleInputChange}
                  placeholder="ค้นหาเลขที่ใบจอง"
                  colorScheme="iBlue"
                />
              </FormControl>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  ชื่อเกษตรกร
                </FormLabel>
                <Input
                  name="FarmerName"
                  value={filterData.FarmerName}
                  onChange={handleInputChange}
                  placeholder="ค้นหาชื่อเกษตรกร"
                  colorScheme="iBlue"
                />
              </FormControl>
            </HStack>
            <Box w="100%" textAlign="right">
              <HStack spacing={5} justifyContent="flex-end">
                <Clear onClick={handleClearSearch} />
                <Search onClick={handleSearch} isLoading={isLoading} />
              </HStack>
            </Box>
          </VStack>
        </Box>

        <HStack w="100%" justify="flex-end">
          <IconButton.Add onClick={handleCreateNew} label="สร้างใบจองใหม่" />
        </HStack>

        {isLoading ? (
          <Spinner size="md" />
        ) : reservations.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลใบจองวัคซีนตามเงื่อนไขที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={reservations}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default StaffVaccineReservationList;
